import { format, parseISO } from 'date-fns';
import { de, enUS, fr } from 'date-fns/locale';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ImageComponent } from '@/components/Image';
import { Locale as ELocale } from '@/helpers/languages';
import { groupMember } from '@/helpers/user';
import {
  MissionMode,
  type QuestMissionWithMode,
} from '@/modules/promotions/helpers';
import { useGetCommonStore } from '@/store';
import { MissionTooltip } from '../MissionTooltip';

import { ScMission } from './styled';

const LOCALE_MAP: Record<string, Locale> = {
  [ELocale.GERMAN]: de,
  [ELocale.FRENCH_CANADA]: fr,
  [ELocale.FRENCH_SWITZERLAND]: fr,
};

interface MissionProps extends QuestMissionWithMode {
  isVipOrBonusHunter: boolean;
}

export const Mission: React.FC<MissionProps> = ({
  date,
  mode,
  title,
  type,
  terms,
  mission,
  prizeUrl,
  prizeType,
  missionUrl,
  vipMission,
  completeImg,
  missionPerGroup,
  isSpecialMission,
  missionUrlPerGroup,
  isVipOrBonusHunter,
  missionExternalUrls,
}) => {
  const {
    i18n: { language },
  } = useTranslation();
  const locale = LOCALE_MAP[language] || enUS;
  const useCommonStore = useGetCommonStore();

  const isAuthenticated = useCommonStore.use.isAuthenticated();
  const user = useCommonStore.use.user();

  const foregroundImage = useMemo(() => {
    const template = (str: string) =>
      `/quest-xmas/mission/${isSpecialMission ? 'special-' : ''}${str}.webp`;

    if (isSpecialMission) {
      switch (mode) {
        case MissionMode.TodayCompleted:
          return completeImg;
        case MissionMode.PastCompleted:
          return completeImg;
        default:
          return template('mission-available2');
      }
    }

    switch (mode) {
      case MissionMode.TodayCompleted:
      case MissionMode.PastCompleted:
        return completeImg;
      case MissionMode.TodayActive:
        return template('mission-available');
      case MissionMode.Future:
        return template('mission-locked');
      case MissionMode.PastFailed:
      case MissionMode.TodayFailed:
        return template('mission-failed');
    }
  }, [mode, completeImg, isSpecialMission]);

  const [isOpenedTooltip, setIsOpenedTooltip] = useState(false);

  const toggleTooltip = () => {
    if (!isAuthenticated) return;

    setIsOpenedTooltip((prev) => !prev);
  };

  const missionDate = format(parseISO(date), 'dd MMM', { locale });

  const missionUrlByGroups = useMemo(() => {
    if (!missionUrlPerGroup) return;
    const urlByGroupKey = Object.keys(missionUrlPerGroup).find((key) =>
      groupMember(key)(user)
    );

    if (urlByGroupKey) return missionUrlPerGroup[urlByGroupKey];
    return missionUrlPerGroup.default;
  }, [missionUrlPerGroup, user]);

  const missionByGroups = useMemo(() => {
    if (!missionPerGroup) return '';
    const urlByGroupKey = Object.keys(missionPerGroup).find((key) =>
      groupMember(key)(user)
    );

    if (urlByGroupKey) return missionPerGroup[urlByGroupKey];
    return missionPerGroup.default;
  }, [missionPerGroup, user]);

  return (
    <ScMission isSpecialMission={isSpecialMission} mode={mode}>
      <div className="display" onClick={toggleTooltip}>
        {/* <ImageComponent
          className="background"
          height={104}
          src={`/quest/mission/${isSpecialMission ? 'special-mission' : 'mission'}-bg.webp`}
          width={104}
          height={104}
          alt="mission background"
        /> */}
        <ImageComponent
          alt="mission foreground"
          className="foreground"
          height={80}
          src={foregroundImage}
          width={80}
        />
      </div>
      {/* <h1 className="date">{missionDate}</h1> */}
      {isOpenedTooltip && (
        <MissionTooltip
          completeImg={completeImg}
          date={missionDate}
          isSpecialMission={isSpecialMission}
          mission={
            (isVipOrBonusHunter ? vipMission : mission) ?? missionByGroups
          }
          missionExternalUrls={missionExternalUrls}
          missionUrl={missionUrl ?? missionUrlByGroups}
          mode={mode}
          onClose={toggleTooltip}
          prizeType={prizeType}
          prizeUrl={prizeUrl}
          terms={terms}
          title={title}
          type={type}
        />
      )}
    </ScMission>
  );
};
